/**
 * Custom Icon Component
 * Uses Jonkai Studio custom icons instead of Lucide
 */

import { clsx } from 'clsx';

// Map of all available icons
export const ICON_NAMES = [
  'dashboard', 'brands', 'sparkles', 'layers', 'dna', 'megaphone', 'mail', 'globe',
  'checkbox', 'edit', 'menu', 'calendar', 'calendar-alt', 'bar-chart', 'trending-up',
  'settings', 'settings-alt', 'logout', 'sparkles-alt', 'layers-alt', 'calendar-small',
  'megaphone-alt', 'mail-alt', 'globe-alt', 'zap', 'image', 'film', 'video',
  'megaphone-small', 'film-alt', 'pin', 'trash', 'save', 'send', 'download',
  'refresh', 'arrow-left', 'arrow-right', 'expand', 'x', 'x-alt', 'check',
  'checkbox-checked', 'checkbox-empty', 'loader', 'alert', 'file-text', 'eye',
  'eye-off', 'heart', 'message-circle', 'share', 'eye-views', 'users', 'trending',
  'user', 'search', 'users-group', 'brain', 'stars', 'wand', 'palette',
  'instagram', 'facebook', 'twitter', 'linkedin', 'tiktok', 'holoday', 'type',
  'bell', 'trophy', 'brush', 'key', 'clock', 'rocket', 'document', 'pen',
  // Additional aliases (mapped to existing icons)
  'message', 'smartphone', 'plus', 'copy', 'hash', 'smile', 'target',
  'upload', 'thumbs-down', 'thumbs-up', 'link', 'cursor'
] as const;

export type IconName = typeof ICON_NAMES[number];

// Size presets
const SIZES = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-8 h-8',
  '2xl': 'w-10 h-10',
  '3xl': 'w-12 h-12',
};

export type IconSize = keyof typeof SIZES;

interface IconProps {
  name: IconName;
  size?: IconSize;
  className?: string;
  alt?: string;
}

// Map aliases to actual icon files
const ICON_ALIASES: Record<string, string> = {
  'message': 'message-circle',
  'smartphone': 'dashboard',
  'plus': 'sparkles',
  'copy': 'checkbox',
  'hash': 'layers',
  'smile': 'sparkles',
  'target': 'dna',
  'upload': 'send',
  'thumbs-down': 'x',
  'thumbs-up': 'check',
  'link': 'globe',
  'cursor': 'edit',
  'rocket': 'rocket',
  'document': 'document',
};

export function Icon({ name, size = 'md', className, alt }: IconProps) {
  // Resolve alias to actual icon name
  const resolvedName = ICON_ALIASES[name] || name;

  return (
    <img
      src={`/icons/${resolvedName}.png`}
      alt={alt || name}
      className={clsx(SIZES[size], 'object-contain', className)}
      loading="lazy"
    />
  );
}

// Convenience wrapper components for common icons
// These provide type safety and easier migration from Lucide

export function DashboardIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="dashboard" size={size} className={className} />;
}

export function BrandsIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="brands" size={size} className={className} />;
}

export function SparklesIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="sparkles" size={size} className={className} />;
}

export function LayersIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="layers" size={size} className={className} />;
}

export function DnaIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="dna" size={size} className={className} />;
}

export function MegaphoneIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="megaphone" size={size} className={className} />;
}

export function MailIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="mail" size={size} className={className} />;
}

export function GlobeIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="globe" size={size} className={className} />;
}

export function CalendarIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="calendar" size={size} className={className} />;
}

export function BarChartIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="bar-chart" size={size} className={className} />;
}

export function SettingsIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="settings" size={size} className={className} />;
}

export function LogoutIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="logout" size={size} className={className} />;
}

export function MenuIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="menu" size={size} className={className} />;
}

export function EditIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="edit" size={size} className={className} />;
}

export function TrashIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="trash" size={size} className={className} />;
}

export function SaveIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="save" size={size} className={className} />;
}

export function SendIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="send" size={size} className={className} />;
}

export function DownloadIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="download" size={size} className={className} />;
}

export function RefreshIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="refresh" size={size} className={className} />;
}

export function ArrowLeftIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="arrow-left" size={size} className={className} />;
}

export function ArrowRightIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="arrow-right" size={size} className={className} />;
}

export function XIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="x" size={size} className={className} />;
}

export function CheckIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="check" size={size} className={className} />;
}

export function CheckboxCheckedIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="checkbox-checked" size={size} className={className} />;
}

export function CheckboxEmptyIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="checkbox-empty" size={size} className={className} />;
}

export function LoaderIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="loader" size={size} className={className} />;
}

export function AlertIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="alert" size={size} className={className} />;
}

export function FileTextIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="file-text" size={size} className={className} />;
}

export function EyeIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="eye" size={size} className={className} />;
}

export function EyeOffIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="eye-off" size={size} className={className} />;
}

export function HeartIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="heart" size={size} className={className} />;
}

export function MessageCircleIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="message-circle" size={size} className={className} />;
}

export function ShareIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="share" size={size} className={className} />;
}

export function UsersIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="users" size={size} className={className} />;
}

export function TrendingUpIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="trending-up" size={size} className={className} />;
}

export function UserIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="user" size={size} className={className} />;
}

export function BellIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="bell" size={size} className={className} />;
}

export function ZapIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="zap" size={size} className={className} />;
}

export function ImageIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="image" size={size} className={className} />;
}

export function FilmIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="film" size={size} className={className} />;
}

export function VideoIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="video" size={size} className={className} />;
}

export function BrainIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="brain" size={size} className={className} />;
}

export function PaletteIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="palette" size={size} className={className} />;
}

export function TrophyIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="trophy" size={size} className={className} />;
}

export function SearchIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="search" size={size} className={className} />;
}

// Social platform icons
export function InstagramIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="instagram" size={size} className={className} />;
}

export function FacebookIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="facebook" size={size} className={className} />;
}

export function TwitterIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="twitter" size={size} className={className} />;
}

export function LinkedinIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="linkedin" size={size} className={className} />;
}

export function TiktokIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="tiktok" size={size} className={className} />;
}

// Construction/WIP icon (using alert for now)
export function ConstructionIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="alert" size={size} className={className} />;
}

// Copy icon (using checkbox for now)
export function CopyIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="checkbox" size={size} className={className} />;
}

// Key icon
export function KeyIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="key" size={size} className={className} />;
}

// PenSquare icon (using edit)
export function PenSquareIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="edit" size={size} className={className} />;
}

// Square icon (using checkbox-empty)
export function SquareIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="checkbox-empty" size={size} className={className} />;
}

// CheckSquare icon (using checkbox-checked)
export function CheckSquareIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="checkbox-checked" size={size} className={className} />;
}

// ChevronLeft/Right (using arrows)
export function ChevronLeftIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="arrow-left" size={size} className={className} />;
}

export function ChevronRightIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="arrow-right" size={size} className={className} />;
}

// GripVertical (using menu)
export function GripVerticalIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="menu" size={size} className={className} />;
}

// Loader2 (using loader)
export function Loader2Icon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="loader" size={size} className={className} />;
}

// Clock icon
export function ClockIcon({ size = 'md', className }: Omit<IconProps, 'name'>) {
  return <Icon name="clock" size={size} className={className} />;
}
